import { doRequest, prepareInternalRequest } from "./api";

/**
 * Converts a WebAuthn DOMException thrown by the browser into a user-friendly
 * error object for ErrorDisplay.
 *
 * @param {Error} error The DOMException raised by the WebAuthn API.
 * @param {string} fallbackMessage The fallback message for unexpected errors.
 *
 * @returns {{ message: string, type: string }} The error display object.
 */
function toCeremonyError( error, fallbackMessage ) {
	if ( error && error.name === "NotAllowedError" ) {
		return { message: "The passkey operation was cancelled or timed out. Please try again.", type: "info" };
	}

	if ( error && error.name === "InvalidStateError" ) {
		return { message: "This passkey is already registered on your account.", type: "warning" };
	}

	return { message: fallbackMessage, type: "error" };
}

/**
 * Converts an API error body (returned by `doRequest` on a non-2xx response)
 * into an ErrorDisplay-shaped object. NestJS exception bodies look like
 * `{ statusCode, message, ...extras }` where `message` may be either a string
 * or, for ValidationPipe failures, an array of strings.
 *
 * @param {Object} error The parsed error body.
 * @param {string} fallbackMessage The fallback message when the body has no usable message.
 *
 * @returns {{ message: string, type: string }} The error display object.
 */
function toServerError( error, fallbackMessage ) {
	const raw = error && error.message;
	const message = Array.isArray( raw ) ? raw.join( " " ) : ( raw || fallbackMessage );
	return { message, type: "error" };
}

/**
 * Converts a raw passkey sign-in error to a user-friendly error object for ErrorDisplay.
 *
 * @param {Error|Object} error The raw error (WebAuthn DOMException or API error body).
 *
 * @returns {{ message: string, type: string }} The error display object.
 */
export function toPasskeyLoginError( error ) {
	if ( error && error.name === "NotAllowedError" ) {
		return { message: "The passkey operation was cancelled or timed out. Please try again.", type: "info" };
	}

	return { message: "Passkey sign-in failed. Please try again or use your password.", type: "error" };
}

/**
 * Runs the WebAuthn authentication (sign-in) ceremony:
 *
 * 1. Requests authentication options from the server.
 * 2. Prompts the user to authenticate with their passkey via the browser WebAuthn API.
 * 3. Sends the authentication response to the server for verification.
 *
 * On success the server sets the `access_token` cookie and returns the token in
 * the response body. Errors are rethrown raw; map them with `toPasskeyLoginError`.
 *
 * @param {Object} [params] Optional parameters.
 * @param {string} [params.turnstileToken] Cloudflare Turnstile verification token.
 *
 * @returns {Promise<{ access_token: string }>} The verification response body.
 */
export async function performPasskeyAuthentication( { turnstileToken } = {} ) {
	// Step 1: Get authentication options
	const optionsRequest = prepareInternalRequest(
		"auth/passkeys/authentication-options/",
		"POST",
		{ turnstileToken },
		{ credentials: "include" },
	);
	const { options, challengeKey } = await doRequest( optionsRequest );

	// Step 2: Authenticate with browser WebAuthn API
	const { startAuthentication } = await import( "@simplewebauthn/browser" );
	const authResponse = await startAuthentication( { optionsJSON: options } );

	// Step 3: Verify with server.
	// No turnstileToken here: a Turnstile token is single-use and was already spent on the
	// authentication-options request above. This endpoint is instead protected server-side by
	// the one-time challenge (challengeKey), which is consumed atomically before verification.
	const verifyRequest = prepareInternalRequest(
		"auth/passkeys/verify-authentication/",
		"POST",
		{ response: authResponse, challengeKey },
		{ credentials: "include" },
	);
	return doRequest( verifyRequest );
}

/**
 * Thrown when the server requires a TOTP code to authorize registration.
 * The caller is expected to re-prompt the user and call registerPasskey again with the code.
 */
export class TotpRequiredError extends Error {
	/**
	 * @param {string} [message] Optional error message.
	 */
	constructor( message ) {
		super( message || "TOTP code required" );
		this.name = "TotpRequiredError";
	}
}

/**
 * Runs the full WebAuthn registration ceremony and returns the registered passkey.
 *
 * Throws an object shaped like `{ message, type }` on failure, suitable for
 * passing directly into ErrorDisplay, except for TotpRequiredError, which the
 * caller should detect to prompt for a TOTP code.
 *
 * @param {Object} params Registration parameters.
 * @param {string} params.name The name for the passkey.
 * @param {string} params.password The account password, used to authorize the registration.
 * @param {string} [params.totpCode] The TOTP code, required when 2FA is enabled.
 *
 * @returns {Promise<Object>} The registered passkey.
 */
export default async function registerPasskey( { name, password, totpCode } ) {
	let challengeKey;
	let options;
	try {
		const optionsRequest = prepareInternalRequest( "passkeys/registration-options", "POST", { password, totpCode } );
		( { options, challengeKey } = await doRequest( optionsRequest ) );
	} catch ( error ) {
		if ( error && error.requireTotp ) {
			throw new TotpRequiredError( error.message );
		}
		throw toServerError( error, "Failed to register passkey. Please try again." );
	}

	let registrationResponse;
	try {
		const { startRegistration } = await import( "@simplewebauthn/browser" );
		registrationResponse = await startRegistration( { optionsJSON: options } );
	} catch ( error ) {
		throw toCeremonyError( error, "Failed to register passkey. Please try again." );
	}

	try {
		const verifyRequest = prepareInternalRequest( "passkeys/verify-registration", "POST", {
			response: registrationResponse,
			challengeKey,
			name: name || "Passkey",
		} );
		return await doRequest( verifyRequest );
	} catch ( error ) {
		throw toServerError( error, "Failed to register passkey. Please try again." );
	}
}
